/**
 * Página de Auditoria de Limites de Tenants
 * Monitora discrepâncias entre recursos contratados vs utilizados
 * Permite correção de discrepâncias desativando membros/inboxes
 * 
 * Versão: 1.1.0
 * Data: 2026-01-08
 */
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Scale, 
  AlertTriangle, 
  CheckCircle2, 
  Users, 
  Inbox, 
  Download,
  Search,
  Filter,
  Wrench
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  useAdminTenantLimitsAudit, 
  useAuditSummary,
  TenantLimitAudit 
} from '@/hooks/useAdminTenantLimitsAudit';
import { DiscrepancyFixModal } from '@/components/admin/DiscrepancyFixModal';

// Tipo de filtro
type FilterType = 'all' | 'discrepancy' | 'compliant';

/**
 * Componente de célula para exibir uso vs limite
 * Formato: [Uso] / [Plano] (+[Add-ons]) (X inativos)
 * v1.1.1: Adicionado suporte para exibir membros inativos
 */
function LimitCell({ 
  current, 
  planLimit, 
  addonQuantity, 
  overLimit,
  inactiveCount = 0
}: { 
  current: number; 
  planLimit: number; 
  addonQuantity: number;
  overLimit: number;
  inactiveCount?: number;
}) {
  const hasDiscrepancy = overLimit > 0;
  
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className={hasDiscrepancy ? 'text-destructive font-semibold' : 'text-foreground'}>
        {current}
      </span>
      <span className="text-muted-foreground">/</span>
      <span className="text-muted-foreground">{planLimit}</span>
      {addonQuantity > 0 && (
        <span className="text-primary text-xs">(+{addonQuantity})</span>
      )}
      {hasDiscrepancy ? (
        <Badge variant="destructive" className="text-xs ml-1">
          +{overLimit}
        </Badge>
      ) : (
        <CheckCircle2 className="h-3.5 w-3.5 text-green-500 ml-1" />
      )}
      {/* v1.1.1: Exibe quantidade de membros inativos */}
      {inactiveCount > 0 && (
        <Badge variant="outline" className="text-xs ml-1 text-muted-foreground border-muted-foreground/30">
          {inactiveCount} inativo{inactiveCount > 1 ? 's' : ''}
        </Badge>
      )}
    </div>
  );
}

/**
 * Cards de resumo da auditoria
 */
function SummaryCards({ 
  total, 
  withDiscrepancy, 
  compliant,
  isLoading 
}: { 
  total: number; 
  withDiscrepancy: number; 
  compliant: number;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-3">
        {[1, 2, 3].map(i => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total de Tenants</CardTitle>
          <Scale className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{total}</div>
        </CardContent>
      </Card>
      
      <Card className={withDiscrepancy > 0 ? 'border-destructive/50' : ''}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Com Discrepância</CardTitle>
          <AlertTriangle className={`h-4 w-4 ${withDiscrepancy > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${withDiscrepancy > 0 ? 'text-destructive' : ''}`}>
            {withDiscrepancy}
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Conformes</CardTitle>
          <CheckCircle2 className="h-4 w-4 text-green-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">{compliant}</div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Função para exportar dados para CSV
 */
function exportToCSV(data: TenantLimitAudit[]) {
  const headers = [
    'Tenant',
    'Slug',
    'Status',
    'Plano',
    'Membros Atual',
    'Membros Plano',
    'Membros Add-ons',
    'Membros Efetivo',
    'Membros Excedido',
    'Inboxes Atual',
    'Inboxes Plano',
    'Inboxes Add-ons',
    'Inboxes Efetivo',
    'Inboxes Excedido',
    'Tem Discrepância'
  ];

  const rows = data.map(t => [
    t.tenant_name,
    t.tenant_slug,
    t.tenant_status,
    t.plan_name,
    t.current_members,
    t.plan_max_agents,
    t.addon_agents,
    t.effective_max_agents,
    t.members_over_limit,
    t.current_active_inboxes,
    t.plan_max_inboxes,
    t.addon_inboxes,
    t.effective_max_inboxes,
    t.inboxes_over_limit,
    t.has_discrepancy ? 'Sim' : 'Não'
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `auditoria-limites-${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
}

/**
 * Componente principal da página
 */
export default function TenantLimitsAudit() {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useAdminTenantLimitsAudit();
  const summary = useAuditSummary(data);
  
  // Estados de filtro
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  
  // Estado do modal de correção
  const [fixModalOpen, setFixModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<TenantLimitAudit | null>(null);
  
  // Handler para abrir modal de correção
  const handleFixDiscrepancy = useCallback((tenant: TenantLimitAudit) => {
    setSelectedTenant(tenant);
    setFixModalOpen(true);
  }, []);
  
  // Handler para sucesso na correção
  const handleFixSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  // Dados filtrados
  const filteredData = useMemo(() => {
    if (!data) return [];
    
    return data.filter(tenant => {
      // Filtro por texto
      const matchesSearch = 
        tenant.tenant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.tenant_slug.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtro por tipo
      const matchesFilter = 
        filterType === 'all' ||
        (filterType === 'discrepancy' && tenant.has_discrepancy) ||
        (filterType === 'compliant' && !tenant.has_discrepancy);
      
      return matchesSearch && matchesFilter;
    });
  }, [data, searchTerm, filterType]);

  // Erro ao carregar
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Auditoria de Limites</h1>
          <p className="text-muted-foreground">Erro ao carregar dados de auditoria</p>
        </div>
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Auditoria de Limites</h1>
        <p className="text-muted-foreground">
          Monitore discrepâncias entre recursos contratados vs utilizados por tenant
        </p>
      </div>

      {/* Cards de Resumo */}
      <SummaryCards 
        total={summary.total_tenants}
        withDiscrepancy={summary.tenants_with_discrepancy}
        compliant={summary.tenants_compliant}
        isLoading={isLoading}
      />

      {/* Filtros e Ações */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou slug..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={filterType} onValueChange={(v) => setFilterType(v as FilterType)}>
          <SelectTrigger className="w-full sm:w-48">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filtrar por..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="discrepancy">Com Discrepância</SelectItem>
            <SelectItem value="compliant">Conformes</SelectItem>
          </SelectContent>
        </Select>

        <Button 
          variant="outline" 
          onClick={() => data && exportToCSV(data)}
          disabled={!data || data.length === 0}
        >
          <Download className="h-4 w-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Tabela de Dados */}
      <Card>
        <CardHeader>
          <CardTitle>Tenants</CardTitle>
          <CardDescription>
            Comparativo de recursos contratados (plano + add-ons) vs uso real
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm || filterType !== 'all' 
                ? 'Nenhum tenant encontrado com os filtros aplicados'
                : 'Nenhum tenant cadastrado'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Plano</TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      Membros
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <Inbox className="h-4 w-4" />
                      Inboxes
                    </div>
                  </TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((tenant) => (
                  <TableRow 
                    key={tenant.tenant_id}
                    className={tenant.has_discrepancy ? 'bg-destructive/5' : ''}
                  >
                    <TableCell>
                      <div>
                        <div className="font-medium">{tenant.tenant_name}</div>
                        <div className="text-xs text-muted-foreground">{tenant.tenant_slug}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{tenant.plan_name}</Badge>
                    </TableCell>
                    <TableCell>
                      <LimitCell 
                        current={tenant.current_members}
                        planLimit={tenant.plan_max_agents}
                        addonQuantity={tenant.addon_agents}
                        overLimit={tenant.members_over_limit}
                        inactiveCount={tenant.inactive_members}
                      />
                    </TableCell>
                    <TableCell>
                      <LimitCell 
                        current={tenant.current_active_inboxes}
                        planLimit={tenant.plan_max_inboxes}
                        addonQuantity={tenant.addon_inboxes}
                        overLimit={tenant.inboxes_over_limit}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      {tenant.has_discrepancy ? (
                        <Badge variant="destructive">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Discrepância
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          OK
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {tenant.has_discrepancy && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleFixDiscrepancy(tenant)}
                        >
                          <Wrench className="h-3.5 w-3.5 mr-1" />
                          Corrigir
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Legenda */}
      <div className="text-xs text-muted-foreground">
        <strong>Legenda:</strong> [Uso Atual] / [Limite Plano] (+[Add-ons Contratados])
      </div>
      
      {/* Modal de correção de discrepância */}
      <DiscrepancyFixModal
        open={fixModalOpen}
        onOpenChange={setFixModalOpen}
        tenant={selectedTenant}
        onSuccess={handleFixSuccess}
      />
    </div>
  );
}
